/**
 * 회차 개념이 없는 수강권(무제한·대관) 판정 — **판정은 여기 한 곳에서만.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 이 파일이 따로 있나
 * ─────────────────────────────────────────────────────────────
 * 라포에는 회차라는 개념이 없는 상품이 있습니다. 대관·월 무제한 같은 것들인데,
 * 수강권 모델에 회차가 필수라서 **운영에서 `totalSession` 을 999 로 넣어 등록합니다.**
 * 스키마에 "무제한" 이라는 값이 따로 없어서 생긴 운영 관례입니다.
 *
 * 화면은 그 999 를 회차로 믿고 그대로 계산했습니다. 실제로 이런 화면이 나왔습니다.
 *
 *   이충훈 회원 · 상품 「대관」 · 남은 수업 999회
 *   지켜야 할 매출 30,549,420원
 *   "잔여 999회가 3회에 가까워지면 상담을 준비하세요"
 *
 * 3천만 원은 999 × 회당 단가입니다. 존재하지 않는 돈이고, 그게 센터 합계에
 * 들어가 있었습니다. 안내 문장도 999 가 3 에 가까워질 일이 없으니 영원히 틀립니다.
 *
 * ─────────────────────────────────────────────────────────────
 * 판정을 한 곳에 두는 이유
 * ─────────────────────────────────────────────────────────────
 * `totalSession >= 999` 를 화면마다 적으면 반드시 갈립니다. 어떤 화면은 999 만,
 * 어떤 화면은 998 도 무제한으로 보게 되고, 그러면 같은 회원이 목록에서는
 * 「무제한」인데 합계에는 3천만 원으로 들어갑니다 —
 * 지금 고치는 버그가 다른 얼굴로 돌아옵니다.
 *
 * 그래서 `memberValue`·`adapters`·`passOrder`·`memberSignals`·화면이 전부
 * 이 파일의 `isSessionless` 하나만 봅니다.
 *
 * ⚠️ 이 숫자는 **우리가 정한 규칙이 아니라 운영 관례를 읽는 장치**입니다.
 *    서버가 나중에 명시 플래그(`sessionless`)를 주면 그걸 **우선**합니다 —
 *    관례로 추측한 값보다 서버가 단정한 값이 언제나 정확합니다.
 *    (백엔드 요청: BACKEND_HANDOFF_DAILY_OPS_BRIEFING.md 8장)
 */

/**
 * 이 회차 수 이상이면 무제한으로 본다.
 *
 * 999 는 운영에서 실제로 쓰는 값입니다. `>` 가 아니라 `>=` 인 이유는,
 * 999 자체가 센티넬이기 때문입니다. 그리고 상한을 두지 않는 이유는
 * 1000·9999 로 등록한 센터가 있어도 전부 같은 뜻이기 때문입니다.
 *
 * ⚠️ 실제 1,000회짜리 수강권이 팔리는 날이 오면 이 판정이 그것까지 삼킵니다.
 *    회당 단가가 있는 상품이라면 금액이 통째로 「산정 불가」가 되므로,
 *    그때는 이 상수가 아니라 **서버 플래그**로 갈아타야 합니다.
 *    (한 회원이 1,000회를 소진하려면 주 3회로 6년이 걸립니다 — 당장은 없습니다)
 */
export const SESSIONLESS_TOTAL_SESSION_MIN = 999;

/**
 * 판정에 필요한 값만.
 *
 * 둘 다 선택입니다 — 구버전 서버 응답이나 회차 정보가 없는 호출부에서도
 * 그냥 넘길 수 있어야 합니다. 모르면 `false`(= 보통 수강권)로 봅니다.
 * 모를 때 무제한이라고 단정하면 멀쩡한 수강권의 금액이 통째로 사라집니다.
 */
export type SessionlessInput = {
    /** 등록상 총 회차. 서버 `membership.totalSession` */
    totalSession?: number | null;
    /**
     * 서버가 명시한 무제한 여부.
     *
     * 아직 서버가 보내지 않습니다(키 없음 = 모름). 보내기 시작하면
     * **회차 추측보다 이 값이 이깁니다** — `false` 로 명시한 999회짜리
     * 진짜 수강권도 그대로 회차 상품으로 다룰 수 있어야 합니다.
     */
    sessionless?: boolean | null;
};

/** 회차 개념이 없는 수강권인가 */
export const isSessionless = (m: SessionlessInput): boolean => {
    /* 서버가 단정했으면 그대로 따른다 (관례 추측보다 우선) */
    if (m.sessionless != null) return m.sessionless;
    return m.totalSession != null && m.totalSession >= SESSIONLESS_TOTAL_SESSION_MIN;
};

/**
 * 잔여 회차 자리에 쓸 말.
 *
 * 「999회」라고 쓰면 원장님은 그것을 실제 잔여로 읽습니다.
 * 「—」로 비우면 회차 정보가 없는 수강권(진짜 모르는 것)과 섞입니다.
 * 무제한은 **모르는 게 아니라 아는 사실**이므로 그대로 적습니다.
 */
export const SESSIONLESS_REMAIN_TEXT = '무제한';

/** 남은 수업 칸 옆에 붙일 설명 */
export const SESSIONLESS_REMAIN_HINT = '회차 제한 없는 수강권';
